import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowUpwardOutlined as ArrowUpward, ArrowDownwardOutlined as ArrowDownward } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchAllRatings, MessageRating, updateRatingStatus } from '../../store/slices/adminSlice';
import { parseUTCDate, safeFormatDate } from '../../utils/dateUtils';
import Pagination from '../common/Pagination';

interface FeedbackMonitorProps {
  users: any[];
}

const FeedbackMonitor: React.FC<FeedbackMonitorProps> = ({ users }) => {
  const dispatch = useAppDispatch();
  const { messageRatings, loading, error } = useAppSelector((state: any) => state.admin);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    ratingId: string;
    newStatus: 'pending' | 'reviewed';
    action: string;
  }>({ open: false, ratingId: '', newStatus: 'pending', action: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const cardRef = useRef<HTMLDivElement>(null);
  

  const userById = React.useMemo(() => {
    const map: Record<number, any> = {};
    users.forEach((u: any) => { map[u.id] = u; });
    return map;
  }, [users]);

  useEffect(() => {
    dispatch(fetchAllRatings(undefined));
  }, [dispatch]);

  const handleUserFilter = (userId: number | '') => {
    // Preserve scroll position
    const scrollPosition = window.pageYOffset;
    setSelectedUserId(userId);
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  const handleRatingStatusUpdate = (ratingId: string, newStatus: 'pending' | 'reviewed') => {
    const action = newStatus === 'reviewed' ? 'mark as reviewed' : 'mark as pending';
    setConfirmDialog({
      open: true,
      ratingId,
      newStatus,
      action
    });
  };

  const confirmStatusUpdate = async () => {
    try {
      await dispatch(updateRatingStatus({ 
        ratingId: confirmDialog.ratingId, 
        status: confirmDialog.newStatus 
      })).unwrap();
      setConfirmDialog({ open: false, ratingId: '', newStatus: 'pending', action: '' });
    } catch (error) {
      console.error('Failed to update rating status:', error);
    }
  };

  const cancelStatusUpdate = () => {
    setConfirmDialog({ open: false, ratingId: '', newStatus: 'pending', action: '' });
  };

  const filteredRatings = messageRatings.filter((rating: MessageRating) => 
    selectedUserId === '' || rating.user_id === selectedUserId
  );

  // Include BOTH text and non-text ratings (thumbs/stars without comment).
  const sortedRatings = filteredRatings
    .sort((a: MessageRating, b: MessageRating) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'user':
          aValue = userById[a.user_id]?.username || '';
          bValue = userById[b.user_id]?.username || '';
          break;
        case 'rating':
          aValue = a.rating_value;
          bValue = b.rating_value;
          break;
        case 'feedback_text':
          aValue = (a.feedback_text || '').trim();
          bValue = (b.feedback_text || '').trim();
          break;
        case 'message_id':
          aValue = a.message_id;
          bValue = b.message_id;
          break;
        case 'created_at':
        default:
          aValue = parseUTCDate(a.created_at).getTime();
          bValue = parseUTCDate(b.created_at).getTime();
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const paginatedRatings = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedRatings.slice(startIndex, endIndex);
  }, [sortedRatings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedRatings.length / itemsPerPage);

  return (
    <Card ref={cardRef}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Message Feedback Monitor
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by User</InputLabel>
            <Select
              value={selectedUserId}
              label="Filter by User"
              onChange={(e) => handleUserFilter(e.target.value as number | '')}
            >
              <MenuItem value="">All Users</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <LinearProgress />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('user')} 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      User
                      {getSortIcon('user')}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('rating')} 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Rating
                      {getSortIcon('rating')}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('feedback_text')} 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Feedback Text
                      {getSortIcon('feedback_text')}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('message_id')} 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Message ID
                      {getSortIcon('message_id')}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('created_at')} 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Submitted
                      {getSortIcon('created_at')}
                    </Box>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRatings.map((rating: MessageRating) => (
                  <TableRow key={rating.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: userById[rating.user_id]?.role === 'admin' ? 'primary.main' : 'grey.500' }}>
                          {(userById[rating.user_id]?.username || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 400 }}>
                            {userById[rating.user_id]?.username || `User #${rating.user_id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {userById[rating.user_id]?.email || ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={rating.rating_type === 'thumbs' ? (rating.rating_value === 1 ? '👍' : '👎') : `${rating.rating_value}/5`}
                          color={rating.rating_type === 'thumbs' ? (rating.rating_value === 1 ? 'success' : 'error') : (rating.rating_value >= 4 ? 'success' : rating.rating_value >= 3 ? 'warning' : 'error')}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 400,
                          color: rating.feedback_text && rating.feedback_text.trim() !== '' ? 'text.primary' : 'text.secondary',
                          fontStyle: rating.feedback_text && rating.feedback_text.trim() !== '' ? 'normal' : 'italic',
                        }}
                      >
                        {rating.feedback_text && rating.feedback_text.trim() !== '' ? rating.feedback_text : '— (No comment)'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {rating.message_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {safeFormatDate(rating.created_at, 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {rating.status === 'pending' ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleRatingStatusUpdate(rating.id, 'reviewed')}
                          >
                            Mark Reviewed
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleRatingStatusUpdate(rating.id, 'pending')}
                          >
                            Mark Pending
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedRatings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No message ratings found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && sortedRatings.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={sortedRatings.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={cancelStatusUpdate}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} this feedback rating?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelStatusUpdate} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmStatusUpdate} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FeedbackMonitor;